import { get, post } from './client';

// ============================================================================
// Types
// ============================================================================

export type TaskPriority = 'low' | 'normal' | 'high' | 'critical';

export type QueueHealthStatus = 'healthy' | 'backlogged' | 'no_consumers' | 'not_found';

export type TaskStatus = 'pending' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'partial_failure';

// Task-specific options
export interface RepullTimeseriesOptions {
  fund_ids: string[];
  ignore_skip_flag?: boolean;
  verbose?: boolean;
  skip_cleanup?: boolean;
}

export interface RepullEquityTimeseriesOptions {
  equity_ids: string[];
  api_batch_size?: number;
  verbose?: boolean;
  skip_deletion?: boolean;
}

export type TaskOptions = RepullTimeseriesOptions | RepullEquityTimeseriesOptions | Record<string, unknown>;

export interface TriggerTaskRequest {
  task_type: string;
  priority?: TaskPriority;
  options: TaskOptions;
}

export interface TriggerTaskResponse {
  task_id: string;
  task_type: string;
  queue: string;
  status: 'queued';
  created_at: string;
  created_by: string;
}

export interface TaskStep {
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'stalled';
  detail: string;
}

export interface TaskStatusResponse {
  task_id: string;
  task_type: string;
  status: TaskStatus;
  progress: number;
  status_message?: string;
  steps?: TaskStep[];
  parent_task_id?: string;
  child_task_ids?: string[];
  created_at: string;
  updated_at?: string;
  started_at?: string;
  last_heartbeat_at?: string;
  completed_at?: string;
  is_stalled?: boolean;
  /** Seconds without heartbeat/progress before this task type is marked stalled. */
  stall_timeout_seconds?: number;
  error?: string;
  result?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  /** Duration in milliseconds (from backend) */
  duration_ms?: number;
  /** Human-readable duration string (e.g., "13.15s", "2m 30.5s", "1h 5m") */
  duration_formatted?: string;
}

export interface TaskListResponse {
  tasks: TaskStatusResponse[];
  total?: number;
  has_more: boolean;
}

export type TaskListSortField =
  | 'created_at'
  | 'started_at'
  | 'updated_at'
  | 'task_type'
  | 'status'
  | 'progress'
  | 'duration_ms';

export interface TaskListParams {
  status?: TaskStatus | TaskStatus[];
  exclude_status?: TaskStatus | TaskStatus[];
  task_type?: string | string[];
  exclude_task_type?: string | string[];
  search?: string;
  sort_by?: TaskListSortField;
  sort_order?: 'asc' | 'desc';
  skip?: number;
  limit?: number;
  include_total?: boolean;
}

export const DEFAULT_TASK_LIST_PAGE_SIZE = 30;

export interface QueueInfo {
  name: string;
  messages: number;
  consumers: number;
  status: QueueHealthStatus;
}

export type TaskArgumentWidget =
  | 'text'
  | 'checkbox'
  | 'number'
  | 'date'
  | 'date_list'
  | 'string_list'
  | 'resource_picker'
  | 'etl_step'
  | 'json';

export interface TaskArgument {
  name: string;
  type: string;
  required: boolean;
  widget: TaskArgumentWidget;
  label?: string;
  resource_type?: string;
  default?: unknown;
}

export interface TaskType {
  type: string;
  description: string;
  typical_runtime: string;
  category: string;
  default_queue: string;
  required_options?: string[];
  note?: string;
  arguments?: TaskArgument[];
}

export interface TaskTypesResponse {
  task_types: TaskType[];
  queues: string[];
}

/** Options for load_ms_fund_universe (Morningstar MASTER from Azure). */
export interface LoadMsFundUniverseOptions {
  blob_path?: string;
  drop_existing_backup?: boolean;
}

// ============================================================================
// Task Categories (for UI organization)
// ============================================================================

export const TASK_CATEGORIES = {
  etl: {
    label: 'ETL Tasks',
    description: 'Extract, Transform, Load operations',
    color: 'blue',
  },
  snapshot: {
    label: 'Snapshot Tasks',
    description: 'Snapshot management operations',
    color: 'emerald',
  },
  data: {
    label: 'Data Tasks',
    description: 'Data validation and management',
    color: 'amber',
  },
  report: {
    label: 'Report Tasks',
    description: 'Report generation and processing',
    color: 'purple',
  },
  custom: {
    label: 'Custom Tasks',
    description: 'User-defined custom tasks',
    color: 'slate',
  },
} as const;

export type TaskCategory = keyof typeof TASK_CATEGORIES;

// ============================================================================
// Priority Configuration (for UI)
// ============================================================================

export const PRIORITY_CONFIG: Record<TaskPriority, { label: string; description: string; color: string }> = {
  low: {
    label: 'Low',
    description: 'Background tasks, not time-sensitive',
    color: 'slate',
  },
  normal: {
    label: 'Normal',
    description: 'Standard priority (default)',
    color: 'blue',
  },
  high: {
    label: 'High',
    description: 'Important tasks, process before normal',
    color: 'amber',
  },
  critical: {
    label: 'Critical',
    description: 'Urgent tasks, highest priority',
    color: 'red',
  },
};

// ============================================================================
// Status Configuration (for UI)
// ============================================================================

export const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'slate' },
  queued: { label: 'Queued', color: 'slate' },
  running: { label: 'Running', color: 'blue' },
  completed: { label: 'Completed', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
  cancelled: { label: 'Cancelled', color: 'yellow' },
  partial_failure: { label: 'Partial failure', color: 'amber' },
};

export const TERMINAL_STATUSES = new Set<TaskStatus>(['completed', 'failed', 'cancelled', 'partial_failure']);

// ============================================================================
// API Functions
// ============================================================================

/**
 * Trigger a new task
 * Requires: admin:write permission
 */
export const triggerTask = async (request: TriggerTaskRequest): Promise<TriggerTaskResponse> => {
  return post<TriggerTaskResponse>('/admin/tasks/trigger', request);
};

/**
 * Get task status by ID
 * Requires: admin:write permission
 */
export const getTaskStatus = async (taskId: string): Promise<TaskStatusResponse> => {
  return get<TaskStatusResponse>(`/admin/tasks/status/${taskId}`);
};

/**
 * List recent tasks with optional filters
 * Requires: admin:write permission
 */
export const listTasks = async (params?: TaskListParams): Promise<TaskListResponse> => {
  const queryParams = new URLSearchParams();
  const statuses = params?.status
    ? Array.isArray(params.status)
      ? params.status
      : [params.status]
    : [];
  for (const value of statuses) {
    queryParams.append('status', value);
  }

  const taskTypes = params?.task_type
    ? Array.isArray(params.task_type)
      ? params.task_type
      : [params.task_type]
    : [];
  for (const value of taskTypes) {
    queryParams.append('task_type', value);
  }

  const excludeStatuses = params?.exclude_status
    ? Array.isArray(params.exclude_status)
      ? params.exclude_status
      : [params.exclude_status]
    : [];
  for (const value of excludeStatuses) {
    queryParams.append('exclude_status', value);
  }

  const excludeTaskTypes = params?.exclude_task_type
    ? Array.isArray(params.exclude_task_type)
      ? params.exclude_task_type
      : [params.exclude_task_type]
    : [];
  for (const value of excludeTaskTypes) {
    queryParams.append('exclude_task_type', value);
  }

  if (params?.search?.trim()) {
    queryParams.append('search', params.search.trim());
  }
  if (params?.sort_by) {
    queryParams.append('sort_by', params.sort_by);
  }
  if (params?.sort_order) {
    queryParams.append('sort_order', params.sort_order);
  }
  if (params?.skip !== undefined) {
    queryParams.append('skip', String(params.skip));
  }
  if (params?.include_total) {
    queryParams.append('include_total', 'true');
  }
  queryParams.append('limit', String(params?.limit ?? DEFAULT_TASK_LIST_PAGE_SIZE));

  const queryString = queryParams.toString();
  const url = `/admin/tasks/list?${queryString}`;
  return get<TaskListResponse>(url);
};

/**
 * Get queue status for all queues
 * Requires: admin:write permission
 * Note: This endpoint returns an array directly, not wrapped in an object
 */
export const getQueueStatus = async (): Promise<QueueInfo[]> => {
  const response = await get<QueueInfo[]>('/admin/tasks/queues');
  return Array.isArray(response) ? response : [];
};

/**
 * Get available task types
 * Requires: admin:write permission
 */
export const getTaskTypes = async (): Promise<TaskTypesResponse> => {
  return get<TaskTypesResponse>('/admin/tasks/types');
};

// ============================================================================
// API Object Export (for consistency with other services)
// ============================================================================

export const taskManagerAPI = {
  triggerTask,
  getTaskStatus,
  listTasks,
  getQueueStatus,
  getTaskTypes,
};
